import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { FaArrowRight, FaVideo, FaLightbulb, FaMedal, FaHourglass, FaSuitcase, FaRegBookmark } from 'react-icons/fa';
import { FaLock } from 'react-icons/fa6';
import { useTheme } from '../UI/Theme/ThemeContext';
import { checkAuthenticatedUser, startExamSession } from '../../utils/client-functions';
import Rules from '../MockTests/Rules';
import useExamStartGate from '../../hooks/useExamStartGate';
import ExamReadyModal from '../MockTests/ExamReadyModal';
import { Modal } from '../UI/Modal/Modal';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainModal from '../UI/Modal/MainModal';
import { ButtonSubmitGreen, ButtonThin } from '../UI/Buttons/Buttons.js';
import Button from '../UI/Buttons/Button';


// Modal wrapper is provided by MainModal — internal content uses simple containers

const Header = styled.div`
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom: 8px;
`;

const PhaseHero = styled.div`
  display:flex;
  gap: 18px;
  align-items:flex-start;
  margin-bottom: 18px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Thumb = styled.div`
  width: 260px;
  height: 150px;
  border-radius: 8px;
  background: linear-gradient(180deg,#e6f7f7,#f7fffe);
  display:flex;
  align-items:center;
  justify-content:center;
  color: #07393a;
  font-weight:700;
  flex-shrink:0;
  @media (max-width:900px) {
    width: 100%;
    height: 120px;
  }
`;

const Content = styled.div`
  flex:1;
`;

const StepsColumn = styled.div`
  display:flex;
  flex-direction:column;
  gap:12px;
`;

const StepCard = styled.div`
  display:flex;
  gap:12px;
  align-items:flex-start;
  padding:12px;
  border-radius:8px;
  background: ${props => props.isDark ? 'rgba(255,255,255,0.02)' : 'rgba(3,20,21,0.02)'};
  border: 1px solid rgba(0,0,0,0.04);

  @media (max-width:900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Arrow = styled.div`
  display:flex;
  align-items:center;
  justify-content:center;
  color: #9aaeb0;
  font-size: 18px;
`;

/* CTA replaced by Button components; kept style adjustments via inline styles where needed */

const Minor = styled.div`
  color: ${props => props.isDark ? '#cfcfcf' : '#616161'};
  font-size: .9rem;
`;

const PhaseDetail = ({ phase, phaseIndex, video, onClose, onPhaseUpdated }) => {
  const { themeName } = useTheme();
  const isDark = themeName === 'dark';
  const [startedSteps, setStartedSteps] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [phaseNowCompleted, setPhaseNowCompleted] = useState(Boolean(phase && phase.completed));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [selectedMockStep, setSelectedMockStep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = useState(0);

  const assemblePayloadFromStep = (step) => {
    const subject = step.name || step.title || (step.description ? String(step.description).slice(0,60) : 'Mock Exam');
    const mins = Number(step.timeAllocation) || 45;
    const timeAlloted = mins * 60;
    const description = step.description || step.notes || '';
    const payload = {
      examName: step.name || step.title || subject,
      subject,
      timeAlloted,
      description,
      level: step.level || ''
    };
    if (step.totalQuestions) payload.totalQuestions = step.totalQuestions;
    if (step.passMarkPercent) payload.passMarkPercent = step.passMarkPercent;
    return payload;
  };

  const handleStartExamFromPlan = async () => {
    if (!selectedMockStep) return;
    try {
      await checkAuthenticatedUser();
    } catch (err) {
      return window.location.href = '/popin?mock';
    }

    const payload = assemblePayloadFromStep(selectedMockStep);
    setLoading(true);
    setProgressStatus('Gathering Exam Details...');
    setProgressPercentage(5);
    const token = localStorage?.getItem('token');
      try {
        await startExamSession({
          payload,
          tokenOverride: token,
          onOngoing: async (examDetails) => {
            setLoading(false);
            handleOngoing(examDetails);
            try {
              const examId = examDetails?.examMeta?.examId || examDetails?.examId;
              if (examId && selectedMockStep && typeof selectedMockStep.__stepIndex !== 'undefined') {
                const headers = token ? { 'x-access-token': token } : {};
                await axios.patch(`/user/career-roadmap/phase/${phaseIndex}/step/${selectedMockStep.__stepIndex}/exam`, { examId }, { headers });
                if (typeof onPhaseUpdated === 'function') try { await onPhaseUpdated(); } catch(_) {}
              }
            } catch (err) { console.error('Failed to persist examId on step', err && err.message ? err.message : err); }
          },
          onDone: async (e) => {
            try {
              const parsed = e && e.data ? JSON.parse(e.data) : null;
              const examDetails = parsed || handleDoneEvent(e) || {};
              const examId = examDetails?.examMeta?.examId || examDetails?.examId;
              if (examId && selectedMockStep && typeof selectedMockStep.__stepIndex !== 'undefined') {
                const headers = token ? { 'x-access-token': token } : {};
                try {
                  await axios.patch(`/user/career-roadmap/phase/${phaseIndex}/step/${selectedMockStep.__stepIndex}/exam`, { examId }, { headers });
                  if (typeof onPhaseUpdated === 'function') try { await onPhaseUpdated(); } catch(_) {}
                } catch (err) { console.error('Failed to persist examId on done', err && err.message ? err.message : err); }
              }
            } catch (err) { console.error('onDone handler error', err); }
          },
          onError: (msg) => { setLoading(false); console.error(msg); },
          onJobCreated: (jobId) => { setShowRulesModal(false); setSelectedMockStep(null); },
          setLoading, setProgressStatus, setProgressPercentage
        });
      } catch (e) {
        console.error('[PhaseDetail] start-exam POST failed', e);
        setLoading(false);
      }
  };

  useEffect(() => { setPhaseNowCompleted(Boolean(phase && phase.completed)); }, [phase]);

  useEffect(() => {
    const count = (phase && phase.steps && phase.steps.length) || 0;
    const started = new Array(count).fill(false);
    const completed = new Array(count).fill(false);
    if (Array.isArray(phase && phase.steps)) {
      phase.steps.forEach((s, idx) => {
        const p = typeof s.progress === 'number' ? Number(s.progress) : 0;
        // consider server-persisted `started` flag as well as any progress > 0
        if (p > 0 || (s && s.started === true)) started[idx] = true;
        if (p >= 100) completed[idx] = true;
      });
    }
    setStartedSteps(started);
    setCompletedSteps(completed);
  }, [phase]);

  if (!phase) return null;

  const renderStep = (step, i) => {
    const type = step.type || step.name || 'step';
    const title = step.title || step.name || (step.description ? String(step.description).slice(0,60) : 'Untitled');
    const desc = step.description || step.notes || '';
    // CTA link fallbacks
    let href = '#';
    if (step.link) href = step.link;
    else if (type === 'course') href = `/user/video-search?title=${encodeURIComponent(title)}`;
    else if (type === 'internship') href = `/user/job-hub?type=internship`;
    else if (type === 'examSimulator') href = `/user/exam-simulator?exam=${encodeURIComponent(step.name || title)}`;

    const isFirst = i === 0;

    // If modal was opened with a fetched YouTube video, prefer that for the first course step
    if (isFirst && video && type === 'course' && video.videoId) {
      href = 'https://www.youtube.com/watch?v=' + video.videoId;
    }

    const handleStart = async (index, action) => {
      // mark started locally then perform the action (open or navigate)
      setStartedSteps(prev => {
        const next = prev.slice();
        next[index] = true;
        return next;
      });

      // Persist that the user has started this step (separate from numeric progress)
      try {
        const token = localStorage?.getItem('token');
        if (token) {
          const headers = { 'x-access-token': token };
          // persist start; update parent roadmap if server returns updated state
          axios.patch(`/user/career-roadmap/phase/${phaseIndex}/step/${index}/start`, {}, { headers })
            .then(resp => {
              if (resp && resp.data && resp.data.careerRoadmap && typeof onPhaseUpdated === 'function') {
                try { onPhaseUpdated(); } catch (_) {}
              }
            })
            .catch(err => { console.error('Failed to persist step start', err && err.message ? err.message : err); });
        }
      } catch (e) {
        console.error('Failed to send start persist request', e && e.message ? e.message : e);
      }

      // Do NOT persist a partial numeric progress here. Persisting a low stepProgress
      // (e.g., 10) caused phases to appear partially complete and unlocked the next
      // phase prematurely. We only persist full completion (100) when the user
      // clicks Done. The `started` flag is independent and safe.
      if (typeof action === 'function') action();
    }

    const handleDone = async (index) => {
      // compute new completed array synchronously so we can base logic on it
      const nextCompleted = (Array.isArray(completedSteps) ? completedSteps.slice() : []);
      nextCompleted[index] = true;
      setCompletedSteps(nextCompleted);

      // persist completion (100%) for this step
      let stepPatchSucceeded = false;
      try {
        const token = localStorage?.getItem('token');
        const headers = token ? { 'x-access-token': token } : {};
        await axios.patch(`/user/career-roadmap/phase/${phaseIndex}`, { stepIndex: index, stepProgress: 100 }, { headers });
        stepPatchSucceeded = true;
      } catch (e) {
        console.error('Failed to persist step done', e && e.message ? e.message : e);
      }

      // refresh parent roadmap so progress rings and unlocked phases update
      if (stepPatchSucceeded && typeof onPhaseUpdated === 'function') {
        try { await onPhaseUpdated(); } catch (e) { console.error('onPhaseUpdated failed', e); }
      }

      // after persisting, if all steps are now completed, mark the phase completed
      try {
        const allDone = nextCompleted.every(v => v === true);
        if (allDone && !phase.completed) {
          const token2 = localStorage?.getItem('token');
          const headers2 = token2 ? { 'x-access-token': token2 } : {};
          await axios.patch(`/user/career-roadmap/phase/${phaseIndex}`, { completed: true }, { headers: headers2 });
          // optimistically update UI
          setPhaseNowCompleted(true);
          if (typeof onPhaseUpdated === 'function') {
            try { await onPhaseUpdated(); } catch (e) { console.error('onPhaseUpdated failed', e); }
          }
        }
      } catch (e) {
        console.error('Failed to auto-complete phase', e && e.message ? e.message : e);
      }
    }

    return (
      <React.Fragment key={i}>
        <StepCard isDark={isDark}>
          <div style={{width:56, height:56, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', background: 'rgba(0,0,0,0.03)', fontWeight:800, flexShrink:0}}>{(i+1)}</div>
          <div style={{flex:1, display:'flex', flexDirection:'column', justifyContent:'space-between'}}>
            <div>
              <Typography variant="subtitle1" sx={{fontWeight:800}}>{title}</Typography>
              <Minor isDark={isDark}>{desc}</Minor>
              {step.skills && step.skills.length > 0 && (
                <div style={{marginTop:10, display:'flex', gap:8, flexWrap:'wrap'}}>
                  {step.skills.slice(0,6).map((s,idx) => (
                    <div key={idx} style={{padding:'6px 8px', borderRadius:999, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(3,20,21,0.03)', fontWeight:700, fontSize:'.8rem'}}>{s}</div>
                  ))}
                </div>
              )}
            </div>

            <div style={{display:'flex', justifyContent:'space-between', marginTop:12, alignItems:'center'}}>
              <div style={{display:'flex', alignItems:'center'}}>
                {startedSteps[i] && !completedSteps[i] && (
                  <ButtonThin onClick={() => handleDone(i)} color={'#3E8F93'} border={'1px solid rgba(62,143,147,0.12)'} style={{padding:'8px 12px', borderRadius:8, marginRight:8}}>
                    Done
                  </ButtonThin>
                )}

                {completedSteps[i] && (
                  <ButtonThin color={'#ffffff'} bgColor={'#3E8F93'} style={{padding:'8px 12px', borderRadius:8, marginRight:8}}>
                    Done ✓
                  </ButtonThin>
                )}
              </div>

              <div style={{display:'flex', justifyContent:'flex-end', width: '40%'}}>
                {
                  // determine server progress and local completed/unlocked states
                  (() => {
                    const serverProg = typeof step.progress === 'number' ? Number(step.progress) : 0;
                    const completed = Boolean(completedSteps[i] || serverProg >= 100);
                    const prevServerProg = phase && phase.steps && phase.steps[i-1] && typeof phase.steps[i-1].progress === 'number' ? Number(phase.steps[i-1].progress) : 0;
                    const unlocked = (i === 0) || Boolean(completedSteps[i-1]) || prevServerProg >= 100;

                    // label mapping for start CTA
                    const labelFor = (t) => {
                      if (!t) return 'Start';
                      if (t === 'examSimulator') return 'Take Mock';
                      if (t === 'certification') return 'Start Certification';
                      if (t === 'project') return 'Start Project';
                      if (t === 'internship') return 'Go to Job Hub';
                      if (t === 'course') return 'Start Course';
                      return 'Start';
                    }

                    // icon mapping matches the one used in CareerRoadmap phase cards
                    const iconMap = {
                      course: FaVideo,
                      project: FaLightbulb,
                      certification: FaMedal,
                      examSimulator: FaHourglass,
                      internship: FaSuitcase
                    };
                    const TypeIcon = iconMap[type] || FaRegBookmark;

                    if (completed) {
                      // If this is a mock/exam step and we have an examId, provide a proper `link` so
                      // the shared `Button` component (which renders a react-router `Link`) navigates
                      // correctly instead of the Link (with no `to`) overriding an imperative navigate.
                      const examId = step.examId || step.mockId || (step.resource && step.resource.examId) || (step.meta && step.meta.examId) || (step.exam && step.exam.examId);
                      if (type === 'examSimulator' && examId) {
                        const to = `/user/dashboard/mock/exam-insight?examId=${encodeURIComponent(examId)}`;
                        return (
                          <Button secondaryLight $border={'1px solid #3E8F93'} color={'#3E8F93'} link={to} target={'_blank'}>
                            View Completed Step
                          </Button>
                        );
                      }

                      if (type === 'course') {
                        // prefer any explicit step.link, then a persisted chosenVideo on the step, then the modal-provided `video` for first step, then fallback to internal search
                        const courseHref = step.link || (step.chosenVideo && (step.chosenVideo.videoId ? `https://www.youtube.com/watch?v=${step.chosenVideo.videoId}` : step.chosenVideo.url)) || (isFirst && video && video.videoId ? `https://www.youtube.com/watch?v=${video.videoId}` : `/user/video-search?title=${encodeURIComponent(title)}`);
                        return (
                          <Button secondaryLight $border={'1px solid #3E8F93'} color={'#3E8F93'} link={courseHref} target={'_blank'}>
                            View Completed Step
                          </Button>
                        );
                      }

                      return (
                        <Button secondaryLight $border={'1px solid #3E8F93'} color={'#3E8F93'} clickHandle={() => {}}>
                          View Completed Step
                        </Button>
                      );
                    }

                    if (unlocked) {
                      // unlocked but not completed: show main green CTA
                      const label = labelFor(type);
                      // compute action for the CTA: open Rules+start flow for examSimulator, otherwise default navigation
                      const action = () => {
                          if (type === 'examSimulator') {
                          // include description, timeAlloted and step index for Rules modal
                          const desc = step.description || step.notes || '';
                          const timeAlloted = (Number(step.timeAllocation) || 30) * 60;
                          setSelectedMockStep({ ...step, description: desc, timeAlloted, __stepIndex: i });
                          setShowRulesModal(true);
                        } else if (href && href !== '#') {
                          if (type === 'course') window.open(href, '_blank'); else window.location.href = href;
                        }
                      };

                      return (
                        <div style={{width: '100%'}}>
                          <ButtonSubmitGreen onClick={() => handleStart(i, action)}>
                            <TypeIcon style={{marginRight:8}} /> {label}
                          </ButtonSubmitGreen>
                        </div>
                      )
                    }

                    // locked
                    return (
                      <ButtonThin onClick={() => {}} color={'#9e9e9e'} border={'1px solid rgba(0,0,0,0.06)'}>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}><FaLock /> Locked</span>
                      </ButtonThin>
                    )
                  })()
                }
              </div>
            </div>
          </div>
        </StepCard>

        {i < (phase.steps || []).length - 1 && (
          <Arrow><FaArrowRight /></Arrow>
        )}
      </React.Fragment>
    )
  }

  return (
    <MainModal isOpen={true} onClose={onClose} width="980px">
      <div>
        <Header>
          <div style={{display:'flex', gap:12, alignItems:'center'}}>
            <div>
              <Typography variant="h4" sx={{fontWeight:700}}>{phase.phaseName}</Typography>
              <Minor isDark={isDark}>{phase.focus || phase.milestone}</Minor>
            </div>
          </div>
        </Header>

        <PhaseHero>
          <Thumb aria-hidden>
            {video && video.thumbnail ? (
              <img src={video.thumbnail} alt={video.title || 'Course thumbnail'} style={{width:'100%', height:'100%', objectFit:'cover', borderRadius:8}} />
            ) : (
              phase.phaseName ? String(phase.phaseName).slice(0,24) : 'Phase'
            )}
          </Thumb>
          <Content>
            <Typography variant="body1" sx={{mb:1}}>{video && video.title ? video.title : (phase.description || phase.milestone || phase.focus)}</Typography>
            <Minor isDark={isDark}>{phase.durationWeeks ? `${phase.durationWeeks} week(s) • ` : ''}Suggested milestone: {phase.milestone || 'Progress through the steps below'}</Minor>
            {video && video.description && (
              <div style={{marginTop:10, color: isDark ? '#dcdcdc' : '#444'}}>{String(video.description).slice(0,420)}</div>
            )}
          </Content>
        </PhaseHero>

        <StepsColumn>
          {(phase.steps || []).map((s, i) => renderStep(s, i))}
        </StepsColumn>

        {showRulesModal && selectedMockStep && (
          <Rules
            examName={selectedMockStep.name || selectedMockStep.title || phase.phaseName}
            subject={selectedMockStep}
            closeModalsAndReset={() => { setShowRulesModal(false); setSelectedMockStep(null); }}
            handleStartExam={handleStartExamFromPlan}
          />
        )}

        {loading && (
          <Modal
            header4={`PROCESSING... DO NOT RELOAD`}
            header3={progressStatus}
            progress={progressPercentage}
            forceWhite={true}
          />
        )}

        {examReady && (
          <ExamReadyModal
            isOpen={examReady}
            onClose={closeGate}
            onStart={confirmStartExam}
            examLabel={selectedMockStep?.title || phase.phaseName}
          />
        )}

        <div style={{display:'flex', justifyContent:'flex-end', marginTop:18}}>
          <ButtonThin onClick={onClose} color={'#9e9e9e'} border={'1px solid rgba(0,0,0,0.06)'}>Close</ButtonThin>
        </div>
      </div>
    </MainModal>
  )
}

export default PhaseDetail;
